import React, { useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Stethoscope, ChevronDown, LogOut, User, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useSignOut } from '../../hooks/Auth/useSignout';
import { logout } from '../../utils/auth';
import { useDispatch } from "react-redux";
import Preloader from "../Preloader";

interface NavLink {
  to: string;
  text: string;
}

interface DropdownItem {
  to?: string;
  onClick?: () => void;
  icon: React.ReactNode;
  text: string;
  disabled?: boolean;
  className?: string;
}

const desktopLinks: NavLink[] = [
  { to: "/find-therapist", text: "Find a Therapist" },
  { to: "/blog", text: "Blog" },

];

// Animation variants
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    scale: 0.95,
    transition: { duration: 0.15 }
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.15, ease: "easeOut" }
  }
};

const mobileMenuVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { 
    height: "auto", 
    opacity: 1,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  
  const { handleSignOut, isLoading: signOutLoading, error } = useSignOut();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Memoize user parsing to avoid repeated localStorage calls
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  // Extract user role with fallback
  // Extract user role with proper formatting
  const userRole = useMemo(() => {
    const role = user?.['custom:user_role'] || user?.role || user?.userType || user?.custom?.role;
    
    switch (role) {
      case 'practice_admin':
        return 'Practice Admin';
      case 'intake_coordinator':
        return 'Intake Coordinator';
      default:
        return 'Platform Admin';
    }
  }, [user]);

  const isLoading = localLoading || signOutLoading;

  const handleLogout = useCallback(async () => {
    setLocalLoading(true);
    setIsDropdownOpen(false);
    setIsMenuOpen(false); // Close mobile menu too
    
    try {
      await dispatch(handleSignOut as any);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLocalLoading(false);
    }
  }, [dispatch, handleSignOut]);

  // Optimized click outside handlers
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  
  const dropdownRef = useClickOutside<HTMLDivElement>(closeDropdown);
  const menuRef = useClickOutside<HTMLDivElement>(closeMenu);

  // Memoized computed values
  const userInitials = useMemo(() => {
    if (!user?.given_name && !user?.family_name) {
      if (user?.email) {
        return user.email.substring(0, 2).toUpperCase();
      }
      return 'U';
    }
    return `${user.given_name?.[0] || ''}${user.family_name?.[0] || ''}`;
  }, [user?.given_name, user?.family_name, user?.email]);

  // Get user's full name
  const userFullName = useMemo(() => {
    const firstName = user?.given_name || user?.firstName || '';
    const lastName = user?.family_name || user?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user?.email?.split('@')[0] || 'User';
  }, [user?.given_name, user?.family_name, user?.firstName, user?.lastName, user?.email]);

  // Memoized dropdown items
  const dropdownItems = useMemo((): DropdownItem[] => [
    // User info header (non-clickable)
    {
      icon: <User className="h-4 w-4 text-purple-600" />,
      text: userFullName,
      className: "border-b border-gray-100 pb-2 mb-2 cursor-default hover:bg-transparent bg-gradient-to-r from-purple-50 to-indigo-50"
    },
    {
      to: "/practice",
      icon: <User className="h-4 w-4 text-blue-600" />,
      text: "Dashboard"
    },
    {
      to: "/practice/settings",
      icon: <Settings className="h-4 w-4 text-gray-600" />,
      text: "Settings"
    },
    {
      onClick: handleLogout,
      icon: <LogOut className="h-4 w-4 text-red-500" />,
      text: isLoading ? "Logging out..." : "Logout",
      disabled: isLoading,
      className: "border-t border-gray-100 mt-1 pt-1 hover:bg-red-50"
    }
  ], [userFullName, handleLogout, isLoading]);

  // Memoized desktop navigation
  const renderDesktopMenu = useMemo(() => (
    desktopLinks.map(({ to, text }) => (
      <Link
        key={to}
        to={to}
        className="relative text-gray-600 hover:text-purple-500 px-3 py-2 
                   text-sm font-medium transition-colors duration-200 group"
      >
        {text}
        <span className="absolute bottom-0 left-3 w-0 h-0.5 bg-purple-500 
                       transition-all duration-300 group-hover:w-[calc(100%-1.5rem)]" />
      </Link>
    ))
  ), []);

  // Memoized dropdown render
  const renderDropdown = useMemo(() => (
    <AnimatePresence>
      {isDropdownOpen && (
        <motion.div
          variants={dropdownVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl 
                   ring-1 ring-gray-100 z-50 py-1"
        >
          {dropdownItems.map((item, index) => (
            <div key={index} className={item.className}>
              {item.to ? (
                <Link
                  to={item.to}
                  className="flex items-center px-4 py-3 text-gray-700 
                           hover:bg-gray-50 transition-colors duration-200"
                  onClick={closeDropdown}
                >
                  {item.icon}
                  <span className="ml-3">{item.text}</span>
                </Link>
              ) : item.onClick ? (
                <button
                  onClick={item.onClick}
                  disabled={item.disabled}
                  className="w-full text-left px-4 py-3 text-gray-700 
                           hover:bg-red-50 flex items-center 
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors duration-200"
                >
                  {item.icon}
                  <span className="ml-3 text-red-600 font-medium">{item.text}</span>
                </button>
              ) : (
                // User info display (non-clickable)
                <div className="flex items-center px-4 py-3 text-gray-700">
                  {item.icon}
                  <div className="ml-3">
                    <span className="font-semibold block text-gray-900">{item.text}</span>
                    <span className="text-xs text-purple-600 capitalize font-medium">{userRole}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  ), [isDropdownOpen, dropdownItems, closeDropdown]);

  // Show preloader for initial loading
  if (localLoading && !user) {
    return <Preloader />;
  }
  
  return (
    <>
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              {/* <Stethoscope className="h-8 w-8 text-purple-600" /> */}
              <span className="text-xl font-semibold text-gray-900">
                Therapist<span className="text-purple-600">Direct</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {renderDesktopMenu}
              
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 focus:outline-none 
                             focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                             rounded-full transition-all duration-200"
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="true"
                  >
                    <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center 
                                  justify-center text-purple-600 font-medium text-sm
                                  ring-2 ring-transparent hover:ring-purple-200 
                                  transition-all duration-200">
                      {userInitials}
                    </div>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-600 transition-transform duration-200
                        ${isDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {renderDropdown}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 
                           rounded-lg hover:bg-purple-700 transition-colors duration-200 
                           shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 
                           focus:ring-offset-2"
                >
                  Professionals - Sign up and Get Started
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-600 hover:text-purple-600 transition-colors
                         rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Optimized Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="md:hidden overflow-hidden border-t border-gray-100"
            >
              <div className="px-4 pt-2 pb-4 space-y-1">
                {user ? (
                  <>
                    {/* User Profile Display */}
                    <div className="px-4 py-3 border-b border-gray-100 mb-2">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center 
                                      justify-center text-purple-600 font-medium text-sm">
                          {userInitials}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-gray-900 block">
                            {userFullName}
                          </span>
                          <p className="text-xs text-purple-600 capitalize font-medium">
                            {userRole}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Settings */}
                    <Link
                      to="/practice/settings"
                      className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-blue-50 
                               transition-colors duration-200 flex items-center space-x-3 
                               hover:text-blue-700"
                      onClick={closeMenu}
                    >
                      <Settings className="h-5 w-5 text-blue-600" />
                      <span>Settings</span>
                    </Link>

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      disabled={isLoading}
                      className="w-full text-left px-4 py-3 rounded-lg text-red-600 
                               hover:bg-red-50 flex items-center space-x-3 
                               disabled:opacity-50 disabled:cursor-not-allowed
                               transition-colors duration-200 hover:text-red-700 font-medium"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>{isLoading ? "Logging out..." : "Logout"}</span>
                    </button>
                  </>
                ) : (
                  /* Show login/register if not authenticated */
                  <Link
                    to="/login"
                    className="block px-4 py-3 rounded-lg bg-purple-50 text-purple-600 
                             hover:bg-purple-100 transition-colors duration-200"
                    onClick={closeMenu}
                  >
                    Login
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Global Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-white/80 
                     backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent 
                          rounded-full animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};